/**
 * URL allowlist for values that reach an iframe `src` or `window.open`.
 *
 * Several viewer entry points read a URL from the query string
 * (`/DV_Viewer?viewer_url=...`) and hand it straight to an iframe. A crafted
 * link could therefore put an arbitrary origin -- or a `javascript:` URL --
 * inside a frame that the SPA also exchanges postMessage traffic with. See
 * safeUrl.test.js for the cases this is required to reject.
 *
 * The accept rule is derived from what the backend actually emits, not guessed.
 * `POST /viewers/launch_viewer/` builds every URL from `request.get_host()`:
 *
 *   https://<host>[:<port>]/pme/
 *   https://<host>[:<port>]/slicer/
 *   https://<host>[:<port>]/ohif/{viewer,segmentation,microscopy}?...
 *   https://<host>[:<port>]/orthanc/kitware/volview?...        (orthanc is a PATH)
 *   https://<host>[:<port>]/orthanc/stone-webviewer/index.html?...
 *
 * all same-origin. The port is env-driven (NGINX_HTTPS_PORT), so it can differ
 * from the page's, and CVAT is built client-side as
 * `https://${window.location.hostname}:19444`. Hence: match on HOSTNAME, allow
 * any port, and do not require an exact origin match.
 */

/** @returns {string|null} the resolved absolute URL, or null if not allowed. */
export function resolveAllowedViewerUrl(raw) {
  if (typeof raw !== 'string') return null;
  const candidate = raw.trim();
  if (!candidate) return null;

  const page = typeof window !== 'undefined' ? window.location : null;
  if (!page) return null;

  let url;
  try {
    // Relative values ("/pme/", "/DV2?x=1") resolve against the page origin.
    url = new URL(candidate, page.origin);
  } catch {
    return null; // unparseable
  }

  // 1. Scheme: kills javascript:, data:, blob:, file:, vbscript:
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  // 2. No silent downgrade from an https page.
  if (page.protocol === 'https:' && url.protocol !== 'https:') return null;

  // 3. Userinfo confusion -- "https://trusted-host@evil.example/". The hostname
  //    check below already catches this because the parser assigns
  //    hostname=evil.example, but reject explicitly rather than relying on that.
  if (url.username || url.password) return null;

  // 4. Same hostname, any port. Protocol-relative "//evil.example/x" resolves to
  //    an external hostname and is rejected here.
  if (url.hostname !== page.hostname) return null;

  return url.toString();
}

/**
 * True when a viewer URL points at the trame/slicer app.
 *
 * DvViewer previously decided this with `decodeURIComponent(param).includes('/slicer/')`,
 * a substring test that `https://evil.example/slicer/` satisfies. Checking the
 * parsed pathname of an already-allowlisted URL removes that.
 */
export function isSlicerViewerUrl(raw) {
  const resolved = resolveAllowedViewerUrl(raw);
  if (!resolved) return false;
  try {
    return new URL(resolved).pathname.startsWith('/slicer/');
  } catch {
    return false;
  }
}

/**
 * Allowlist for links that leave the app entirely (new tab, `noopener`).
 *
 * A DIFFERENT threat model from resolveAllowedViewerUrl, deliberately. A viewer
 * URL is framed inside the SPA and exchanges postMessage traffic with it, so it
 * must be same-host. An external link opens an unrelated tab with no handle back
 * -- restricting it by host is simply wrong: Open Datasets exists to point at
 * TCIA, Kaggle and similar, and its source URL is rendered to the user as the
 * link text.
 *
 * So: reject dangerous schemes, allow any host.
 *
 * @returns {string|null} the resolved URL, or null if the scheme is not http(s).
 */
export function resolveExternalLinkUrl(raw) {
  if (typeof raw !== 'string') return null;
  const candidate = raw.trim();
  if (!candidate) return null;

  const page = typeof window !== 'undefined' ? window.location : null;
  if (!page) return null;

  let url;
  try {
    url = new URL(candidate, page.origin);
  } catch {
    return null;
  }
  // javascript:, data:, blob:, file:, vbscript: -- the only real risk on a link
  // the user is choosing to follow into a fresh tab.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return url.toString();
}

/**
 * True when a `message` event came from an origin we are willing to act on.
 *
 * The viewer iframe's src is already constrained by resolveAllowedViewerUrl, so a
 * legitimate message can only originate from the same hostname. In practice OHIF
 * and trame-slicer are proxied on the SPA's own origin (`/ohif`, `/slicer/`), so
 * this is usually an exact origin match; the hostname rule keeps a non-standard
 * NGINX_HTTPS_PORT working.
 *
 * Pair it with a source check (`ev.source === iframe.contentWindow`) where the
 * message triggers a server-side write -- origin alone still admits any other
 * frame or popup served from the same host.
 */
export function isAllowedMessageOrigin(origin) {
  if (typeof origin !== 'string' || !origin) return false;
  const page = typeof window !== 'undefined' ? window.location : null;
  if (!page) return false;
  if (origin === 'null') return false; // sandboxed/opaque origin
  let url;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (page.protocol === 'https:' && url.protocol !== 'https:') return false;
  return url.hostname === page.hostname;
}

/**
 * The exact origin to use as postMessage's targetOrigin for a viewer iframe,
 * derived from the frame's own src. Returns null when it cannot be determined,
 * in which case the caller should NOT fall back to '*'.
 */
export function viewerTargetOrigin(iframe) {
  const src = iframe && iframe.src;
  if (!src) return null;
  const resolved = resolveAllowedViewerUrl(src);
  if (!resolved) return null;
  try {
    return new URL(resolved).origin;
  } catch {
    return null;
  }
}
